import { DataType } from "../data/GlobalData";
import { isQuestionAnswered } from "./optionalDataUtils";

/**
 * True when reportHealth.reportData.optional has at least one answered daily question.
 * @param {object} [reportData]
 */
export function hasAnsweredDailyOptional(reportData) {
  const optional = reportData?.optional;
  if (!optional || typeof optional !== "object") return false;
  return Object.values(optional).some((field) => isQuestionAnswered(field));
}

/**
 * @param {object} [questionDef]
 * @param {object} [responseData]
 * @returns {string}
 */
export function formatOptionalAnswerDisplay(questionDef, responseData) {
  if (!responseData || responseData.skipped === true) {
    return "Skipped";
  }

  const { value } = responseData;
  if (value === null || value === undefined) {
    return "—";
  }

  const type = questionDef?.type;

  if (type === DataType.yesno) {
    return value ? "Yes" : "No";
  }

  if (type === DataType.multiline) {
    return String(value);
  }

  if (typeof value === "number") {
    const max = questionDef?.maxValue;
    if (typeof max === "number" && max > 0) {
      return `${value} / ${max}`;
    }
    return String(value);
  }

  return String(value);
}

/**
 * Match answered daily optional responses with organization question definitions.
 *
 * @param {Record<string, object>} [optionalResponses] - reportHealth.reportData.optional
 * @param {Array<object>} [organizationOptionals] - organization.optional
 * @returns {Array<{ id: string, name: string, subTitle: string, index: number, type?: string, displayValue: string, notes?: string }>}
 */
export function matchDailyOptionalQuestions(
  optionalResponses,
  organizationOptionals,
) {
  if (!optionalResponses || typeof optionalResponses !== "object") {
    return [];
  }

  const orgQuestions = Array.isArray(organizationOptionals)
    ? organizationOptionals
    : [];

  return Object.entries(optionalResponses)
    .filter(([, responseData]) => isQuestionAnswered(responseData))
    .map(([questionId, responseData]) => {
      const questionDef = orgQuestions.find((q) => q.id === questionId);
      const name =
        questionDef?.name ||
        responseData?.name ||
        responseData?.subTitle ||
        `Question ${questionId}`;

      return {
        id: questionId,
        name,
        subTitle: questionDef?.subTitle || "",
        index: questionDef?.index !== undefined ? questionDef.index : 999,
        type: questionDef?.type,
        notes: responseData?.notes,
        displayValue: formatOptionalAnswerDisplay(questionDef, responseData),
      };
    })
    .sort((a, b) => {
      if (a.index !== b.index) return a.index - b.index;
      return a.name.localeCompare(b.name);
    });
}
